import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthorAvatar from '../../../components/shared/AuthorAvatar';

interface Author {
    _id: string;
    firstName: string;
    lastName: string;
    profilePicture: string | null;
}

interface Dossier {
    _id: string;
    title: string;
    description: string;
    slug: string;
    author: Author;
    bannerUrl?: string;
}

interface FeaturedCarouselProps {
    dossiers: Dossier[];
    loading?: boolean;
}

const FeaturedCarousel: React.FC<FeaturedCarouselProps> = ({ dossiers, loading = false }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [allImagesLoaded, setAllImagesLoaded] = useState(false);
    const navigate = useNavigate();

    // Preload images when data arrives
    useEffect(() => {
        if (!loading && dossiers.length > 0) {
            setAllImagesLoaded(false);

            const imagePromises = dossiers
                .filter(d => d.bannerUrl)
                .map(dossier => {
                    return new Promise<void>((resolve) => {
                        const img = new Image();
                        img.onload = () => resolve();
                        img.onerror = () => resolve();
                        img.src = dossier.bannerUrl!;
                    });
                });

            if (imagePromises.length === 0) {
                setAllImagesLoaded(true);
            } else {
                Promise.all(imagePromises).then(() => setAllImagesLoaded(true));
            }
        }
    }, [loading, dossiers]);

    // Auto-advance carousel every 6 seconds
    useEffect(() => {
        if (isPaused || dossiers.length <= 1) return;

        const timer = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % dossiers.length);
        }, 6000);

        return () => clearInterval(timer);
    }, [currentIndex, isPaused, dossiers.length]);

    const goToSlide = (index: number) => {
        setCurrentIndex(index);
    };

    const goToPrevious = () => {
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? dossiers.length - 1 : prevIndex - 1));
    };

    const goToNext = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % dossiers.length);
    };

    const handleSlideClick = (dossier: Dossier) => {
        navigate(`/dossiers/${dossier._id}`);
    };

    // Pas de dossiers et plus en chargement → rien à afficher
    if (!loading && dossiers.length === 0) return null;

    // Loading skeleton - show while loading data OR loading images
    if (loading || !allImagesLoaded) {
        return (
            <div className="relative w-full rounded-lg overflow-hidden bg-mcdm-primary-200 dark:bg-mcdm-third-400 shadow-lg">
                {/* Skeleton banner */}
                <div className="w-full bg-gradient-to-r from-mcdm-primary-300 to-mcdm-primary-400 dark:from-mcdm-third-400 dark:to-mcdm-third-500 animate-pulse min-h-[233px] md:h-[488px]" />

                {/* Skeleton overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-8">
                    <div className="bg-mcdm-primary-400 dark:bg-mcmd-third-400 h-8 rounded w-3/4 mb-2 animate-pulse" />
                    <div className="bg-mcdm-primary-400 dark:bg-mcmd-third-400 h-5 rounded w-full mb-1 animate-pulse" />
                    <div className="bg-mcdm-primary-400 dark:bg-mcmd-third-400 h-5 rounded w-5/6 mb-2 animate-pulse" />
                    <div className="bg-mcdm-primary-400 dark:bg-mcmd-third-400 h-4 rounded w-1/3 animate-pulse" />
                </div>

                {/* Skeleton navigation arrows */}
                <div className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/50 dark:bg-gray-700/50 p-3 rounded-full opacity-50">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </div>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/50 dark:bg-gray-700/50 p-3 rounded-full opacity-50">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </div>

                {/* Skeleton dot indicator */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                    <div className="h-2 w-8 bg-mcdm-primary-400 rounded-full animate-pulse" />
                </div>
            </div>
        );
    }

    return (
        <div
            className="relative w-full rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-800 shadow-lg"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            {/* Slides container */}
            <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
                {dossiers.map((dossier) => (
                    <div
                        key={dossier._id}
                        className="min-w-full cursor-pointer relative"
                        onClick={() => handleSlideClick(dossier)}
                    >
                        {/* Banner image */}
                        {dossier.bannerUrl ? (
                            <img
                                src={dossier.bannerUrl}
                                alt={dossier.title}
                                className="w-full h-auto object-cover min-h-[233px] md:max-h-[488px]"
                            />
                        ) : (
                            <div className="w-full bg-gradient-to-r from-mcdm-secondary-400 to-mcdm-secondary-500 flex items-center justify-center min-h-[233px] md:h-[488px]">
                                <div className="text-white text-4xl font-bold">{dossier.title}</div>
                            </div>
                        )}

                        {/* Overlay with title and description */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-8">
                            <h2 className="text-mcdm-primary-100 text-3xl mb-2 font-platypi">{dossier.title}</h2>
                            <p className="text-white/90 text-lg mb-2 line-clamp-2">
                                {dossier.description}
                            </p>
                            <div className="flex items-center gap-2">
                                <AuthorAvatar
                                    user={{
                                        _id: dossier.author._id,
                                        firstName: dossier.author.firstName,
                                        lastName: dossier.author.lastName,
                                        email: '',
                                        profilePicture: dossier.author.profilePicture
                                    }}
                                    size="sm"
                                    className="border border-white"
                                    showTooltip={false}
                                />
                                <p className="text-white/75 text-sm">
                                    Par {dossier.author.firstName} {dossier.author.lastName}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Navigation arrows */}
            {dossiers.length > 1 && (
                <>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            goToPrevious();
                        }}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white dark:bg-gray-800/90 dark:hover:bg-gray-800 p-3 rounded-full shadow-lg transition-all duration-200 z-10"
                        aria-label="Précédent"
                    >
                        <svg
                            className="w-6 h-6 text-gray-800 dark:text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            goToNext();
                        }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white dark:bg-gray-800/90 dark:hover:bg-gray-800 p-3 rounded-full shadow-lg transition-all duration-200 z-10"
                        aria-label="Suivant"
                    >
                        <svg
                            className="w-6 h-6 text-gray-800 dark:text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                    </button>

                    {/* Dots navigation */}
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                        {dossiers.map((_, index) => (
                            <button
                                key={index}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    goToSlide(index);
                                }}
                                className={`h-2 rounded-full transition-all duration-300 ${
                                    index === currentIndex ? 'bg-white w-8' : 'bg-white/50 w-2 hover:bg-white/75'
                                }`}
                                aria-label={`Aller à la diapositive ${index + 1}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default FeaturedCarousel;
